import { isCancel } from 'axios'
import { makeAutoObservable } from 'mobx'
import { RequestCancelAxios } from 'shared/api/lib'
import { PerformanceAnalyticsApi } from 'entities/Analytics/api/performanceAnalytics'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type PerformanceAnalyticsKeys } from 'entities/Analytics/api/types'
import { RowPerformanceComparison } from 'entities/Analytics/model/Table/RowPerformanceComparison'
import { SearchTypesEnum } from 'widgets/SearchDropdown'
import { AnalyticsFiltersStore } from 'pages/analytics/store/analyticsFiltersStore'
import { ExportAnalyticsModalStore } from 'pages/analytics/ui/ExportAnalyticsModal'
import { PerformanceChartDataStore } from 'pages/analytics/pages/performance/ui/Chart'
import {
  AnalyticsComparisonTableStore,
  type IAnalyticsComparisonTableConfig,
} from 'pages/analytics/store/analyticsComparisonTableStore'

export type PerformanceComparisonTableStore = AnalyticsComparisonTableStore<
  PerformanceAnalyticsKeys,
  RowPerformanceComparison
>

export type IPerformanceComparisonTableConfig = IAnalyticsComparisonTableConfig<
  PerformanceAnalyticsKeys,
  RowPerformanceComparison
>

export class PerformanceAnalyticsPageStore {
  private readonly _chartDataStore: PerformanceChartDataStore
  private readonly _analyticsFiltersStore: AnalyticsFiltersStore
  private readonly _exportAnalyticsModalStore: ExportAnalyticsModalStore
  private readonly _comparisonTableStore: PerformanceComparisonTableStore
  private readonly _requestCancelAxios = new RequestCancelAxios()

  private _error: string | null = null
  private _pageLoading = false
  private _chartLoading = false

  constructor(entities: IEntitiesStore) {
    this._chartDataStore = new PerformanceChartDataStore()
    this._analyticsFiltersStore = new AnalyticsFiltersStore(entities, {
      onFiltersChange: (loadingType) => this._fetchAnalytics(loadingType),
      additionalFilterTypes: [
        SearchTypesEnum.members,
        SearchTypesEnum.teams,
        SearchTypesEnum.numbers,
        SearchTypesEnum.tags,
        SearchTypesEnum.time_range,
      ],
      breakdownFilterTypes: [SearchTypesEnum.members],
    })
    this._exportAnalyticsModalStore = new ExportAnalyticsModalStore(
      PerformanceAnalyticsApi.exportAnalytics
    )

    const comparisonTableConfig: IPerformanceComparisonTableConfig = {
      tableKey: 'performanceComparisonTable',
      getAnalytics: PerformanceAnalyticsApi.getAnalytics,
      createRow: (data) => new RowPerformanceComparison(data),
      createMetricsRecord: () => ({
        avg_time_to_first_response: 0,
        avg_time_to_response: 0,
        avg_time_to_close: 0,
      }),
    }

    this._comparisonTableStore = new AnalyticsComparisonTableStore(
      comparisonTableConfig,
      () => this._analyticsFiltersStore.analyticsFiltersParams,
      () => this._analyticsFiltersStore.breakdownFilterStore.appliedBreakdownFilterType,
      entities
    )
    makeAutoObservable(this)
  }

  init = () => {
    this._analyticsFiltersStore.initReactions()
    this._comparisonTableStore.init()
    this._fetchAnalytics('page')
  }

  private _fetchAnalytics = async (loadingType: 'page' | 'chart' = 'page') => {
    if (loadingType === 'page') {
      this._pageLoading = true
    } else {
      this._chartLoading = true
    }

    try {
      this._error = null
      const { analyticsFiltersParams } = this._analyticsFiltersStore
      const { period } = this._analyticsFiltersStore.chartFilterStore

      this._requestCancelAxios.init()

      await PerformanceAnalyticsApi.preloadAnalytics(analyticsFiltersParams, {
        cancelToken: this._requestCancelAxios.token,
      })

      const { data } = await PerformanceAnalyticsApi.getAnalytics(analyticsFiltersParams, {
        cancelToken: this._requestCancelAxios.token,
      })

      this._chartDataStore.setRawData(data, period)
    } catch (error) {
      if (!isCancel(error)) {
        this._error = 'Failed to load analytics data'
      }
    } finally {
      if (loadingType === 'page') {
        this._pageLoading = false
      } else {
        this._chartLoading = false
      }
    }
  }

  openExportModal = () => {
    const { analyticsFiltersParams } = this._analyticsFiltersStore
    this._exportAnalyticsModalStore.onOpenExport(analyticsFiltersParams)
  }

  dispose = () => {
    this._analyticsFiltersStore.dispose()
    this._requestCancelAxios.reset()
    this._chartDataStore.dispose?.()
    this._comparisonTableStore.dispose()
    this._exportAnalyticsModalStore.dispose()
  }

  get calendarFilterStore() {
    return this._analyticsFiltersStore.calendarFilterStore
  }

  get breakdownFilterStore() {
    return this._analyticsFiltersStore.breakdownFilterStore
  }

  get chartFilterStore() {
    return this._analyticsFiltersStore.chartFilterStore
  }

  get chartDataStore() {
    return this._chartDataStore
  }

  get comparisonTableStore() {
    return this._comparisonTableStore
  }

  get pageLoading() {
    return this._pageLoading
  }

  get chartLoading() {
    return this._chartLoading
  }

  get error() {
    return this._error
  }
}
